"use client"

import React from "react"
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Paper,
  useTheme,
  alpha,
  Divider,
  Slide,
  Fade,
  Avatar,
} from "@mui/material"
import {
  Close as CloseIcon,
  CheckCircle as ConfirmIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Help as HelpIcon,
} from "@mui/icons-material"

// Slide transition for dialog opening
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

// Types of confirmation dialogs with their respective colors and icons
const dialogTypes = {
  info: {
    icon: <InfoIcon />,
    color: "info.main",
    title: "Confirmation",
    confirmText: "Confirm",
    cancelText: "Cancel",
  },
  warning: {
    icon: <WarningIcon />,
    color: "warning.main",
    title: "Warning",
    confirmText: "Proceed",
    cancelText: "Cancel",
  },
  delete: {
    icon: <DeleteIcon />,
    color: "error.main",
    title: "Delete Confirmation",
    confirmText: "Delete",
    cancelText: "Cancel",
  },
  success: {
    icon: <ConfirmIcon />,
    color: "success.main",
    title: "Confirm Action",
    confirmText: "Confirm",
    cancelText: "Cancel",
  },
  question: {
    icon: <HelpIcon />,
    color: "primary.main",
    title: "Confirmation Required",
    confirmText: "Yes",
    cancelText: "No",
  },
}

/**
 * A stunning confirmation dialog component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Function to call when dialog is closed
 * @param {Function} props.onConfirm - Function to call when action is confirmed
 * @param {string} props.title - Dialog title (optional)
 * @param {string|React.ReactNode} props.message - Dialog message content
 * @param {string} props.confirmText - Text for confirm button (optional)
 * @param {string} props.cancelText - Text for cancel button (optional)
 * @param {string} props.type - Dialog type: 'info', 'warning', 'delete', 'success', 'question' (default: 'info')
 * @param {boolean} props.disableBackdropClick - Whether to disable closing on backdrop click (default: false)
 */
const ConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  type = "info",
  disableBackdropClick = false,
}) => {
  const theme = useTheme()
  const dialogConfig = dialogTypes[type] || dialogTypes.info

  const handleClose = (event, reason) => {
    if (disableBackdropClick && reason === "backdropClick") {
      return
    }
    onClose()
  }

  const handleConfirm = () => {
    onConfirm()
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        elevation: 5,
        sx: {
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
        },
      }}
    >
      {/* Improved title section with better proportions */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "160px", // Increased height
            background: `linear-gradient(135deg, ${alpha(theme.palette[dialogConfig.color.split(".")[0]][dialogConfig.color.split(".")[1]], 0.8)} 0%, ${alpha(theme.palette[dialogConfig.color.split(".")[0]].dark, 0.9)} 100%)`,
            zIndex: 0,
          }}
        />

        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            color: "white",
            zIndex: 2,
            bgcolor: alpha("#000", 0.2),
            "&:hover": {
              bgcolor: alpha("#000", 0.3),
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Box
          sx={{
            pt: 1, // Increased top padding
            pb: 1, // Increased bottom padding
            px: 3,
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            minHeight: "160px", // Ensure minimum height
          }}
        >
          <Fade in={open} timeout={700}>
            <Avatar
              sx={{
                width: 60, // Slightly larger avatar
                height: 60, // Slightly larger avatar
                bgcolor: "white",
                color: dialogConfig.color,
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                mb: 1,
                "& .MuiSvgIcon-root": {
                  fontSize: 44, // Larger icon
                },
              }}
            >
              {dialogConfig.icon}
            </Avatar>
          </Fade>

          <Box sx={{ mt: 1, textAlign: "center", color: "white" }}>
            <Typography variant="h5" fontWeight="bold">
              {title || dialogConfig.title}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
              Please review the information below
            </Typography>
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ px: 3, py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            bgcolor: alpha(theme.palette[dialogConfig.color.split(".")[0]].light, 0.1),
            border: "1px solid",
            borderColor: alpha(theme.palette[dialogConfig.color.split(".")[0]].light, 0.2),
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          {typeof message === "string" ? <Typography variant="body1">{message}</Typography> : message}
        </Paper>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2.5, justifyContent: "center", gap: 2 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1, // Added vertical padding
            borderColor: alpha(theme.palette.divider, 0.5),
            color: theme.palette.text.secondary,
            "&:hover": {
              borderColor: theme.palette.text.secondary,
              bgcolor: alpha(theme.palette.divider, 0.1),
            },
          }}
        >
          {cancelText || dialogConfig.cancelText}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          color={dialogConfig.color.split(".")[0]}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1, // Added vertical padding
            boxShadow: `0 4px 12px ${alpha(theme.palette[dialogConfig.color.split(".")[0]][dialogConfig.color.split(".")[1]], 0.3)}`,
            "&:hover": {
              boxShadow: `0 6px 16px ${alpha(theme.palette[dialogConfig.color.split(".")[0]][dialogConfig.color.split(".")[1]], 0.4)}`,
            },
          }}
        >
          {confirmText || dialogConfig.confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ConfirmationDialog

